import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Button, Grid, makeStyles, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import TextInput from 'components/CustomFormComponents/TextInput';
import ButtonSpinner from 'components/ButtonSpinner';

import { createCompany, updateCompany } from 'redux/actions/staffingActions';
import { useStaffingSelector } from 'redux/selectors/staffing/staffingSelectors';
import { selectCompany } from 'redux/selectors/staffing/getCompanies';
import { enqueueSuccessSnackbar } from 'redux/actions/notifierActions';

/** Exact public-company name used by deal validation. Rename is blocked by this string, not id. */
const VOLTAGE_CONTROL_COMPANY_NAME = 'Voltage Control';

/**
 * Staff may paste a HubSpot company URL instead of the numeric record id. Ingest, deal-init, and
 * the HubSpot pill all need the object id only (the last segment of `/record/0-2/{id}`).
 *
 * @param {string} [value] Raw field value from the form.
 * @returns {string} Trimmed id, or empty string when the field was left blank.
 */
const normalizeHubspotCompanyId = value => {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return '';
  }

  // Current HubSpot company UI: /contacts/{portal}/record/0-2/{id}
  const recordMatch = trimmed.match(/\/record\/0-2\/(\d+)/i);
  if (recordMatch) {
    return recordMatch[1];
  }

  // Older bookmarks still use /company/{id}/
  const legacyMatch = trimmed.match(/\/company\/(\d+)/i);
  if (legacyMatch) {
    return legacyMatch[1];
  }

  return trimmed;
};

const useStyles = makeStyles(theme => ({
  dialogActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
  },
  saveButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    width: '200px',
  },
  errorMessage: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
    textAlign: 'right',
    width: '100%',
  },
  formNotice: {
    marginBottom: theme.spacing(1),
  },
  // Same filled Cancel/Save treatment as AddProjectToDealForm so staffing dialogs match.
  cancelButton: {
    width: 105,
    height: 40,
    marginRight: 'auto',
    background: '#9E9E9E',
    color: '#fff',
    fontSize: 14,
    '&:hover': {
      background: '#9E9E9E',
      opacity: 0.9,
    },
  },
  saveButton: {
    width: 105,
    height: 40,
    background: theme.palette.primary.main,
    color: '#fff',
    fontSize: 14,
    '&:hover': {
      background: theme.palette.primary.main,
      opacity: 0.9,
    },
    '&:disabled': {
      background: '#9E9E9E',
      color: '#fff',
    },
  },
}));

/**
 * Shared create/edit form for Company records. Company name min length matches POST /v1/companies
 * (4) and the deal-form inline create guard. Domain is optional and used for transcript routing.
 *
 * @param {{ closeDialog: Function }} props
 */
const AddCompanyForm = ({ closeDialog }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { companyToUpdateId } = useStaffingSelector();

  const getCompany = selectCompany();
  const company = useSelector(state => getCompany(state, companyToUpdateId));

  const formInitialValues = {
    companyName: company ? company.companyName : '',
    domain: company ? company.domain || '' : '',
    hubspotCompanyId: company ? company.hubspotCompanyId || '' : '',
  };

  const formSchema = Yup.object().shape({
    companyName: Yup.string().trim().min(4, 'Must be at least 4 characters').required('Required'),
    domain: Yup.string().trim(),
    hubspotCompanyId: Yup.string().trim(),
  });

  /**
   * Persist via the companies API, then close. Firestore listeners refresh the list; 409
   * duplicate-name errors are snackbar'd in the thunks.
   *
   * @param {{ companyName: string, domain: string, hubspotCompanyId: string }} values
   */
  const handleFormSubmit = values => {
    const companyName = values.companyName.trim();
    const domain = (values.domain || '').trim();
    const hubspotCompanyId = normalizeHubspotCompanyId(values.hubspotCompanyId);
    const isVoltageControl =
      Boolean(companyToUpdateId) && company?.companyName === VOLTAGE_CONTROL_COMPANY_NAME;
    // Create omits empty domain / HubSpot id so ingest can still fill them later. Edit always
    // sends both so clearing a field can persist as an empty string (falsy ids re-enable
    // name-match linking on the next deal). Voltage Control omits companyName so a disabled
    // field cannot PATCH a rename.
    const createPayload = {
      companyName,
      ...(domain ? { domain } : {}),
      ...(hubspotCompanyId ? { hubspotCompanyId } : {}),
    };
    const updatePayload = isVoltageControl
      ? { domain, hubspotCompanyId }
      : { companyName, domain, hubspotCompanyId };

    setIsSubmitting(true);

    const successCallback = () => {
      dispatch(enqueueSuccessSnackbar(companyToUpdateId ? 'Company updated' : 'Company created'));
      closeDialog();
    };

    const request = companyToUpdateId
      ? dispatch(updateCompany(companyToUpdateId, updatePayload, successCallback))
      : dispatch(createCompany(createPayload, successCallback));

    return Promise.resolve(request).finally(() => setIsSubmitting(false));
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={formSchema}
      initialValues={formInitialValues}
      onSubmit={handleFormSubmit}
      key={companyToUpdateId || 'new'}
    >
      {({ submitForm, resetForm, errors, touched, submitCount, values }) => {
        const isVoltageControlName =
          Boolean(companyToUpdateId) && company?.companyName === VOLTAGE_CONTROL_COMPANY_NAME;
        // Name-match warning is only relevant when they have not supplied a HubSpot id (or URL).
        const showCreateNameMatchWarning =
          !companyToUpdateId && !normalizeHubspotCompanyId(values.hubspotCompanyId);

        return (
          <Form style={{ paddingBottom: 20 }}>
            <Grid container spacing={3}>
              {showCreateNameMatchWarning && (
                <Grid item xs={12}>
                  {/* Create does not call HubSpot. Ingest later uses getCompanyByName — Firestore
                      equality on trimmed companyName (case-sensitive). A near-miss name will
                      create a second CR company instead of attaching hubspotCompanyId. Paste an
                      id below to skip that lookup. */}
                  <Alert severity="warning" className={classes.formNotice}>
                    This does not look up or link companies in HubSpot by name. Paste the HubSpot
                    company ID below to link it now. If you leave the ID blank, the first HubSpot
                    deal that comes in for this company will link only if this name matches the
                    company name in HubSpot exactly (capitalization and punctuation). A near-miss
                    name can create a second Control Room company.
                  </Alert>
                </Grid>
              )}
              {Boolean(companyToUpdateId) && !isVoltageControlName && (
                <Grid item xs={12}>
                  {/* Rename is a CR-only display-name change. Drive folders keep their original
                      names; HubSpot is not renamed. Staff should know before they save. */}
                  <Alert severity="warning" className={classes.formNotice}>
                    Renaming this company updates the name on related deals and projects in Control
                    Room. It <b>does not</b> rename Google Drive folders (client projects or
                    contracts) or the matching company in HubSpot. If an attached Drive folder is
                    later missing, a new one may be created under the new name.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextInput
                  name="companyName"
                  type="string"
                  label="Company Name"
                  className="mb-4"
                  required
                  disabled={isVoltageControlName}
                  helperText={
                    isVoltageControlName
                      ? 'Voltage Control cannot be renamed. Public-deal rules key off this name.'
                      : undefined
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  name="domain"
                  type="string"
                  label="Domain"
                  className="mb-4"
                  helperText="Optional. Used to match meeting transcripts to this company (e.g. acme.com)."
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  name="hubspotCompanyId"
                  type="string"
                  label="HubSpot Company ID"
                  className="mb-4"
                  helperText="Optional. Numeric id from the HubSpot company record, or paste the company URL. Linking by id does not require the names to match."
                />
              </Grid>
            </Grid>

            <div className={classes.dialogActions}>
              <Button
                className={classes.cancelButton}
                disabled={isSubmitting}
                onClick={() => {
                  closeDialog();
                  resetForm();
                }}
              >
                CANCEL
              </Button>
              <div className={classes.saveButtonContainer}>
                <Button className={classes.saveButton} disabled={isSubmitting} onClick={submitForm}>
                  SAVE
                  {isSubmitting && <ButtonSpinner />}
                </Button>
                {submitCount > 0 &&
                  Object.keys(errors).length > 0 &&
                  Object.keys(touched).length > 0 && (
                    <Typography color="error" className={classes.errorMessage}>
                      See errors above.
                    </Typography>
                  )}
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

AddCompanyForm.propTypes = {
  closeDialog: PropTypes.func.isRequired,
};

export default AddCompanyForm;
